import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { theme, ConfigProvider } from "antd";
import {
  useNotification,
  useTranslate,
  useWarnAboutChange,
} from "@refinedev/core";
import { Divider, libertifyThemeV2 } from "@libertify/ui";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useCreateProjectWithDocuments } from "pages/media/projects/hooks/useCreateProjectWithDocuments";
import { useApplyGeneralSettings } from "./hooks/useApplyGeneralSettings";
import {
  PlaybookDraftProvider,
  usePlaybookDraft,
  type AddonObject,
} from "./context/PlaybookDraftContext";
import { BUILD_YOUR_OWN_ADDONS, CONFIG_ITEM } from "./data/addons";
import {
  hasIncompletePodcastVoices,
  mergeSettingsBranches,
  seedAddonSettings,
} from "./data/addonApi";
import type { GeneralSettingsFormValues } from "./components/config-cards/types";
import { GENERAL_SETTINGS_DEFAULTS } from "./components/config-cards/defaults";
import { ContentSidebar } from "./components/ContentSidebar";
import { PlaybookCarousel } from "./components/PlaybookCarousel";
import { uploadFileKey } from "./helpers/fileList";
import { CustomizePanel } from "./components/CustomizePanel";
import type { ConfigItemId } from "./components/config-cards/registry";
import { CreateNowBar } from "./components/CreateNowBar";
import { CreateBlockedModal } from "./components/CreateBlockedModal";
import { PLAYBOOK_PROJECT_TYPE, type CarouselPlaybook } from "./types";
import { Page, Header, Title, Row } from "./CreateExperiencePage.styles";
import { usePlaybooksForCarousel } from "./hooks/usePlaybooksForCarousel";
import { usePlaybookRecord } from "./hooks/usePlaybookRecord";
import { useGeneralSeed } from "./hooks/useAddonSeeder";
import { SavePlaybookModal } from "./components/SavePlaybookModal";
import { SavePlaybookBar } from "./components/SavePlaybookBar";
import { mapDraftToPlaybookUpdate } from "./helpers";

/** What the experience is built from: a playbook, or "build your own". */
type PlaybookSelection =
  | { kind: "playbook"; playbook: CarouselPlaybook }
  | { kind: "custom" };

const buildYourOwnDraft = (): AddonObject =>
  Object.fromEntries(
    BUILD_YOUR_OWN_ADDONS.map((id) => [id, seedAddonSettings(id)])
  );

/**
 * The page's content, rendered inside the theme-v2 ConfigProvider so its
 * `theme.useToken()` (and every child's) resolves to the brand tokens.
 */
const CreateExperienceContent: React.FC = () => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const [selection, setSelection] = useState<PlaybookSelection | null>(null);

  // Two views on one page, driven by the URL hash (deep-linkable, back/forward
  // works). The select view owns the documents sidebar; the customize view is a
  // full-page shell (the draft lives in context, so nothing is lost switching).
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.hash === "#customize" ? "customize" : "select";
  const goCustomize = () =>
    navigate({ pathname: location.pathname, hash: "#customize" });
  // Back discards: only the save bar writes, so unsaved edits stay in the
  // draft and become a copy if the user goes on to create an experience.
  const goSelect = () => {
    setEditingPlaybookId(null);
    navigate({ pathname: location.pathname, hash: "" });
  };

  // Which playbook the customize view is editing IN PLACE, as opposed to
  // configuring an experience from. A browser Back out of #customize skips
  // `goSelect`, so the id can outlive the view — `isEditingPlaybook` reads it
  // against `view` to stay false meanwhile, and `reseedDraft` clears it so a
  // later Save can't write the new draft into it.
  // The playbook being edited in place — for another org's, the copy of it.
  const [editingPlaybookId, setEditingPlaybookId] = useState<string | null>(
    null
  );
  // The playbook the CURRENT draft has already been written to. Unlike
  // `editingPlaybookId` it survives leaving the customize view, so
  // `handleCreate` can tell edits that are already persisted from edits that
  // still need a playbook of their own. Cleared whenever the draft is reseeded.
  const [savedPlaybookId, setSavedPlaybookId] = useState<string | null>(null);
  const isEditingPlaybook = view === "customize" && editingPlaybookId !== null;

  const {
    documents,
    hiddenDocKeys,
    addonObject,
    generalSettings,
    isDirty,
    addonsDirty,
    generalDirty,
    seedAddons,
  } = usePlaybookDraft();

  // The only way to reseed the draft. Both ids name the playbook the PREVIOUS
  // draft belonged to, so neither can outlive it — calling `seedAddons`
  // directly is what let a stale `editingPlaybookId` through.
  const reseedDraft = (...args: Parameters<typeof seedAddons>) => {
    seedAddons(...args);
    setEditingPlaybookId(null);
    setSavedPlaybookId(null);
  };
  const { createProjectWithDocuments } = useCreateProjectWithDocuments(
    documents,
    hiddenDocKeys
  );
  // Spans the WHOLE hand-off (playbook + create + upload → prompt → navigate),
  // not just the hook's create+upload window — otherwise the button drops back
  // to idle while the rest of the sequence is still running.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    createPlaybook,
    renamePlaybook,
    discardPlaybook,
    updatePlaybook,
    isUpdating,
  } = usePlaybookRecord();
  const generalSeed = useGeneralSeed();
  // The keep-or-discard prompt runs AFTER the experience exists, so the
  // hand-off waits on it: the project id is parked here until the user
  // resolves the modal, and only then do we navigate.
  const [pendingPlaybookId, setPendingPlaybookId] = useState<string | null>(
    null
  );
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const { applyHeaderLinks } = useApplyGeneralSettings();
  const { open } = useNotification();
  const { trackEvent } = useAnalytics();
  const { paywallFlow, resetDismissed, setGraceProjectId } = useSoftPaywall();

  const { playbooks, isLoading, defaultPlaybook } = usePlaybooksForCarousel();

  // Selecting a (different) playbook reseeds the draft from it, so the
  // customize view and the create payload reflect THAT playbook's add-ons.
  // In-progress edits belong to the previous selection and are dropped by
  // design; re-selecting the current playbook keeps them.
  const selectPlaybook = (playbook: CarouselPlaybook) => {
    if (
      selection?.kind === "playbook" &&
      selection.playbook.id === playbook.id
    ) {
      return;
    }
    reseedDraft(playbook.addonDraft, playbook.generalDraft);
    setSelection({ kind: "playbook", playbook });
  };

  // Saves the name/description edited inline in the customize view. Throwing
  // would surface as an unhandled rejection inside the panel's save, so the
  // failure is toasted here and the editor is told it is over.
  const savePlaybookDetails = async (
    name: string,
    description: string | null
  ) => {
    if (!editingPlaybookId) return;
    try {
      await updatePlaybook(editingPlaybookId, { name, description });
    } catch (error) {
      console.error("Error saving playbook details:", error);
      open?.({
        type: "error",
        message: t("createExperience.errors.playbookRenameFailed"),
      });
    }
  };

  // The ⋮ menu's Edit: opens the playbook itself, so a save carries over to
  // every future experience built from it.
  // Reseeds unconditionally: `selectPlaybook` skips it for the playbook
  // already selected, which would leave a stale draft in the editor.
  const editPlaybook = (playbook: CarouselPlaybook) => {
    reseedDraft(playbook.addonDraft, playbook.generalDraft);
    setSelection({ kind: "playbook", playbook });
    // After the reseed, which clears it. `savedPlaybookId` stays cleared —
    // nothing has been written until Save.
    setEditingPlaybookId(playbook.id);
    goCustomize();
  };

  // The save bar's action. Marks the draft as living in the playbook, so a
  // later Create builds from it rather than copying it (see `handleCreate`).
  const savePlaybookChanges = async () => {
    if (!editingPlaybookId) return;
    try {
      await updatePlaybook(
        editingPlaybookId,
        mapDraftToPlaybookUpdate(addonObject, generalSettings)
      );
      setSavedPlaybookId(editingPlaybookId);
      goSelect();
    } catch (error) {
      console.error("Error saving playbook:", error);
      open?.({
        type: "error",
        message: t("createExperience.errors.playbookSaveFailed"),
      });
    }
  };

  // The card's Customize: configures an EXPERIENCE from the playbook, never the
  // playbook itself — that is the ⋮ menu's Edit. So it writes nothing here, and
  // the edits become a copy at create time exactly like build-your-own's do,
  // keep-or-discard prompt included. The pick is left untouched either way.
  const customizePlaybook = (playbook: CarouselPlaybook) => {
    selectPlaybook(playbook);
    // Also for the playbook just edited in place: from here its edits belong to
    // the experience, so they must become a copy instead of going back into it.
    setEditingPlaybookId(null);
    setSavedPlaybookId(null);
    goCustomize();
  };

  // A deleted playbook can't stay the selection: dropping it here falls back
  // to the default playbook (see `effectiveSelection`). Its draft goes with
  // it — that fallback is implicit, so no pick follows to reseed it, and
  // `savedPlaybookId` would go on naming a playbook that no longer exists,
  // telling `handleCreate` the draft is already saved somewhere it isn't.
  const handlePlaybookDeleted = (id: string) => {
    if (savedPlaybookId === id) setSavedPlaybookId(null);
    if (selection?.kind !== "playbook" || selection.playbook.id !== id) return;
    setSelection(null);
    reseedDraft(
      defaultPlaybook?.addonDraft ?? buildYourOwnDraft(),
      defaultPlaybook?.generalDraft
    );
  };

  const selectBuildYourOwn = () => {
    if (selection?.kind === "custom") return;
    reseedDraft(buildYourOwnDraft());
    setSelection({ kind: "custom" });
  };

  // An untouched "Build your own" pick doesn't survive going back unedited —
  // it collapses to no selection. Only when there's a default to fall back
  // to, though: for an org with no default playbook, "Build your own" is the
  // only option, so it must stay sticky.
  const hasCustomWork = isDirty || addonsDirty;
  const collapsedSelection =
    selection?.kind === "custom" &&
    view === "select" &&
    !hasCustomWork &&
    defaultPlaybook
      ? null
      : selection;

  // No explicit (or effective) choice yet — the default playbook, already
  // shown as selected in the carousel, is implicitly the choice. No click
  // (or seeding) needed: a pristine default pick needs no add-on draft, since
  // `playbookId` alone applies it server-side (see handleCreate below).
  const effectiveSelection: PlaybookSelection | null =
    collapsedSelection ??
    (defaultPlaybook ? { kind: "playbook", playbook: defaultPlaybook } : null);

  // The selection holds a snapshot from when it was picked, so a rename or a
  // description edit would leave the customize title showing the old text.
  // Re-read it from the (invalidated, refetched) list by id, keeping the
  // snapshot only as a fallback while the list is between fetches.
  const selectedSnapshot =
    effectiveSelection?.kind === "playbook"
      ? effectiveSelection.playbook
      : null;
  const selectedPlaybook = selectedSnapshot
    ? (playbooks.find((entry) => entry.id === selectedSnapshot.id) ??
      selectedSnapshot)
    : null;

  // Build-your-own (no playbook) is creatable too — its add-ons come entirely
  // from the draft context. Hidden docs (Extras) don't count — need at least
  // one visible source doc to create.
  const hasVisibleDoc = documents.some(
    (doc) => !hiddenDocKeys.has(uploadFileKey(doc))
  );
  const needsContent = !hasVisibleDoc || effectiveSelection === null;
  // Second blocker, once there IS content: the Podcast add-on needs both
  // speaker roles filled or generation fails server-side.
  const needsPodcastVoices =
    !needsContent && hasIncompletePodcastVoices(addonObject);

  // The customize view's active rail item, lifted here so a blocked "Create
  // now" click can jump it to the tab that unblocks it (Documents, Podcast, …)
  // exactly like a rail click would (see handleBlockedClick below).
  const [activeConfigTab, setActiveConfigTab] = useState<ConfigItemId>(
    CONFIG_ITEM.General
  );

  // "Create now" blocked flow. Every blocker points at the tab that resolves
  // it; CreateNowBar flashes `blockedReason` above the bar on its own. The one
  // exception is missing content in the "select" view, which has no tabs:
  // there a modal reuses the Step 1 upload area, so finishing the flow is
  // identical to finishing it in the sidebar — and the modal IS the message,
  // hence the `false` that tells the bar to skip its card.
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const openConfigTab = (tab: ConfigItemId) => {
    setActiveConfigTab(tab);
    if (view !== "customize") goCustomize();
  };
  const handleBlockedClick = () => {
    if (needsPodcastVoices) {
      // Only reachable from the customize view's Podcast card, so send the
      // user there even when the click came from the carousel.
      openConfigTab(CONFIG_ITEM.Podcast);
      return;
    }
    if (view === "customize") {
      setActiveConfigTab(CONFIG_ITEM.Documents);
      return;
    }
    // No default playbook to imply either — build-your-own is the only
    // option, so commit it explicitly so the modal has something to create
    // with.
    if (!effectiveSelection) selectBuildYourOwn();
    setIsBlockedModalOpen(true);
    return false;
  };
  const blockedModalName =
    selectedPlaybook?.name ??
    (effectiveSelection?.kind === "custom"
      ? t("createExperience.step2.buildTitle")
      : (defaultPlaybook?.name ?? ""));
  const blockedModalDescription =
    selectedPlaybook?.description ??
    (effectiveSelection?.kind === "custom"
      ? t("createExperience.step2.buildDesc")
      : defaultPlaybook?.description);

  // Warn before leaving with in-progress work (content added or customized).
  // Refine's routerProvider guard (warnWhenUnsavedChanges) covers link clicks,
  // programmatic navigation and tab close uniformly once armed. Playbook-edit
  // mode is no exception: the documents and edits behind it are just as
  // losable, and the Back button's Popconfirm can't collide with this — it
  // navigates by hash through react-router, which the guard never sees.
  const { setWarnWhen } = useWarnAboutChange();
  const hasUnsavedWork = documents.length > 0 || isDirty;
  useEffect(() => {
    setWarnWhen(hasUnsavedWork);
    return () => setWarnWhen(false);
  }, [hasUnsavedWork, setWarnWhen]);

  // One source of truth for the disabled state, the blocked click's accessible
  // name and the warning card, so the button can never be blocked without
  // saying why — and never says two different things about one blocker.
  const resolveBlockedReason = (): string | undefined => {
    if (needsContent) {
      // Reaching the customize view requires a pick, so there the only thing
      // that can be missing is a document; the carousel can be missing either.
      return t(
        `createExperience.createBar.${
          view === "customize" ? "docRequiredWarning" : "notReadyHint"
        }`
      );
    }
    if (needsPodcastVoices) {
      return t("createExperience.createBar.podcastVoicesHint");
    }
    return undefined;
  };
  const blockedReason = resolveBlockedReason();
  const ready = blockedReason === undefined;

  // Build-your-own has no playbook to name the copy after; customizing one does.
  const suggestedPlaybookName = selectedPlaybook
    ? t("createExperience.savePlaybookModal.defaultName.derived", {
        name: selectedPlaybook.name,
      })
    : t("createExperience.savePlaybookModal.defaultName.custom");

  const handleCreate = async () => {
    // Editing a playbook is not a route to creating an experience: the bar is
    // gone in that mode, and this guards the paths that don't go through it.
    if (isEditingPlaybook) return;
    if (!effectiveSelection || !ready || isSubmitting) return;
    setIsSubmitting(true);
    // The General branches of the playbook payload, layered exactly like the
    // card displays them: static defaults → org seed (brand kit / avatar) →
    // the user's values. The defaults layer matters for an untouched card —
    // what it shows (language, side panel) must be what the playbook carries.
    const general = mergeSettingsBranches(
      mergeSettingsBranches(
        {
          generation_settings: {
            ...GENERAL_SETTINGS_DEFAULTS.generation_settings,
          },
          functional_settings: {
            ...GENERAL_SETTINGS_DEFAULTS.functional_settings,
          },
        },
        generalSeed
      ),
      generalSettings
    );
    const generalValues = general as Partial<GeneralSettingsFormValues>;
    // Brand kit: the merged general settings win, else the playbook's.
    const draftBrandKitId = generalValues.generation_settings?.brand_kit_id;
    const hasBrandKit = !!draftBrandKitId || !!selectedPlaybook?.hasBrandKit;

    // The customization becomes a real playbook BEFORE the project, so its id
    // can drive creation — the experience is built from the playbook rather
    // than from a draft applied afterwards. A pristine playbook pick makes no
    // record: it already has one. `generalDirty` counts as customization too:
    // the general card's settings branches (language, side panel) only reach
    // the backend through a playbook's general_*_settings keys, so an edit
    // there needs a draft playbook even when the add-ons are pristine.
    // A saved playbook edit is the exception: the edits already live in
    // `savedPlaybookId`, so a record here would duplicate it.
    let draftPlaybookId: string | undefined;
    if (
      effectiveSelection.kind === "custom" ||
      ((addonsDirty || generalDirty) && !savedPlaybookId)
    ) {
      try {
        draftPlaybookId = await createPlaybook(
          addonObject,
          { name: suggestedPlaybookName },
          general
        );
      } catch (error) {
        // The playbook IS the configuration now, so there is nothing to fall
        // back to: creating anyway would silently ship an experience built
        // from no add-ons (build-your-own) or from the un-edited playbook.
        console.error("Error creating playbook:", error);
        open?.({
          type: "error",
          message: t("createExperience.errors.playbookCreateFailed"),
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const project = await createProjectWithDocuments({
        projectType: selectedPlaybook?.projectType ?? PLAYBOOK_PROJECT_TYPE,
        playbookId: draftPlaybookId ?? selectedPlaybook?.id,
        title: generalValues.name?.trim() || undefined,
        description: generalValues.description?.trim() || undefined,
        language: generalValues.generation_settings?.language,
        // User tags: key = value = the typed text (matches the project tag
        // editor's convention, `useProjectTags`).
        tags: (generalValues.tags ?? []).map((tag) => ({
          tag_key: tag,
          tag_value: tag,
        })),
        // Don't regenerate a brand kit the playbook/draft already defines.
        extraMediaSettings: { generate_brandkit: !hasBrandKit },
      });

      if ((generalValues.links ?? []).length > 0) {
        try {
          await applyHeaderLinks(project.id, generalValues.links ?? []);
        } catch (error) {
          // Same hand-off-continues-on-failure policy as the add-on draft above.
          console.error("Error applying header links:", error);
          open?.({
            type: "error",
            message: t("createExperience.errors.headerLinksApplyFailed"),
          });
        }
      }

      resetDismissed();
      if (paywallFlow === "soft_3_exp") {
        setGraceProjectId(project.id);
      }

      // Success path: disarm the leave guard (it would otherwise prompt on
      // this very navigation) and land on the created experience.
      setWarnWhen(false);

      // Ask whether to keep the playbook the experience was built from. The
      // hand-off resumes once the prompt is resolved.
      if (draftPlaybookId) {
        setPendingPlaybookId(draftPlaybookId);
        setCreatedProjectId(project.id);
        return;
      }

      navigate(`/media/projects/${project.id}/experience-v2`);
      // Deliberately NOT clearing isSubmitting here: the button must stay in
      // its loading state until the experience page takes over and this page
      // unmounts.
    } catch (error) {
      console.error("Error creating experience:", error);
      // Nothing was built from it, and the user was never asked to keep it.
      if (draftPlaybookId) void discardPlaybook(draftPlaybookId);
      setIsSubmitting(false);
    }
  };

  const leaveForCreatedExperience = () => {
    setPendingPlaybookId(null);
    if (createdProjectId) {
      navigate(`/media/projects/${createdProjectId}/experience-v2`);
    }
  };

  // Fire-and-forget: the rename outlives this page's unmount (React Query
  // mutations aren't cancelled by it), and the worst case is a playbook left
  // under its suggested name — not worth holding the hand-off for.
  const handleSavePlaybook = (name: string, description?: string) => {
    if (!pendingPlaybookId) return;
    void renamePlaybook(pendingPlaybookId, { name, description }).catch(
      (error) => {
        console.error("Error naming playbook:", error);
      }
    );
    trackEvent(AnalyticsEvents.SettingsPlaybookSave, {
      playbook_id: pendingPlaybookId,
      playbook_name: name,
    });
  };

  const handleDiscardPlaybook = () => {
    if (pendingPlaybookId) void discardPlaybook(pendingPlaybookId);
    leaveForCreatedExperience();
  };

  return (
    <Page $token={token}>
      {view === "customize" ? (
        // The customize step is its own full-page shell (it replaces the select
        // screen). The draft persists in context, so no content is lost.
        <CustomizePanel
          playbook={selectedPlaybook}
          isEditingPlaybook={isEditingPlaybook}
          onSaveDetails={savePlaybookDetails}
          isSavingDetails={isUpdating}
          onBack={goSelect}
          active={activeConfigTab}
          onSelectActive={setActiveConfigTab}
        />
      ) : (
        <>
          <Header>
            <Title $token={token}>{t("createExperience.title")}</Title>
          </Header>
          <Row>
            <ContentSidebar />
            <Divider
              orientation="vertical"
              spacing="none"
              color={token.colorBorder}
            />
            <PlaybookCarousel
              isLoading={isLoading}
              playbooks={playbooks}
              selectedId={selectedPlaybook?.id ?? defaultPlaybook?.id ?? null}
              onSelect={selectPlaybook}
              onEditPlaybook={editPlaybook}
              onCustomizePlaybook={customizePlaybook}
              onPlaybookDeleted={handlePlaybookDeleted}
              onBuildYourOwn={() => {
                selectBuildYourOwn();
                goCustomize();
              }}
            />
          </Row>
        </>
      )}

      {isEditingPlaybook ? (
        // Editing a playbook is not a route to creating an experience, so the
        // create bar gives way to the one action that applies here.
        <SavePlaybookBar
          isSaving={isUpdating}
          onSave={() => {
            void savePlaybookChanges();
          }}
        />
      ) : (
        <CreateNowBar
          blockedReason={blockedReason}
          isCreating={isSubmitting}
          onCreate={() => {
            void handleCreate();
          }}
          onBlockedClick={handleBlockedClick}
        />
      )}
      <SavePlaybookModal
        open={pendingPlaybookId !== null}
        defaultName={suggestedPlaybookName}
        onSave={handleSavePlaybook}
        onDiscard={handleDiscardPlaybook}
        onAcknowledge={leaveForCreatedExperience}
      />
      <CreateBlockedModal
        open={isBlockedModalOpen && !isEditingPlaybook}
        onClose={() => setIsBlockedModalOpen(false)}
        playbookName={blockedModalName}
        playbookDescription={blockedModalDescription}
        ready={ready}
        isCreating={isSubmitting}
        onCreate={() => {
          void handleCreate();
        }}
      />
    </Page>
  );
};

/**
 * Playbook-centric "Create experience" page.
 *
 * Add content (Step 1) + choose a playbook (Step 2), then "Create now" seeds a
 * real project with the playbook and hands off to the existing review/approval
 * screen. The content is themed with theme-v2 so it reads on-brand (Inter +
 * Libertify greens).
 */
export const CreateExperiencePage: React.FC = () => {
  return (
    <ConfigProvider theme={libertifyThemeV2}>
      <PlaybookDraftProvider>
        <CreateExperienceContent />
      </PlaybookDraftProvider>
    </ConfigProvider>
  );
};
